import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.css';
import { Table, Loading } from '../../../../../common/ui';
import { PiSmileySad } from 'react-icons/pi';

const formatAmount = (value) => {
  if (value === null || value === undefined) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const normalized =
      typeof dateValue === 'string' && dateValue.includes(' ')
        ? dateValue.replace(' ', 'T')
        : dateValue;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return dateValue;
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateValue;
  }
};

const renderAmount = (value) => {
  const formatted = formatAmount(value);
  if (!formatted) {
    return <span className={styles.pendingValue}>—</span>;
  }
  return formatted;
};

const renderVariance = (value) => {
  if (value === null || value === undefined) {
    return <span className={styles.pendingValue}>—</span>;
  }
  return formatAmount(value);
};

const renderStatus = (status) => {
  if (!status || status === '-') {
    return <span className={styles.pendingValue}>—</span>;
  }
  return status;
};

const ShiftSummaryTable = memo(
  ({
    data = [],
    isLoading = false,
    error = null,
    pagination = {},
    setSearchParams,
    page = 1,
    limit = 10,
  }) => {
    const transformedData = useMemo(
      () =>
        (data || []).map((item) => ({
          id: item.shiftId,
          shiftStartDateTime: formatDateTime(item.shiftStartDateTime),
          startExpectedBalance: renderAmount(item.start?.expectedBalance),
          startActualBalance: renderAmount(item.start?.actualBalance),
          startVariance: renderVariance(item.start?.variance),
          startStatus: renderStatus(item.start?.status),
          shiftEndDateTime: formatDateTime(item.shiftEndDateTime),
          endExpectedBalance: renderAmount(item.end?.expectedBalance),
          endActualBalance: renderAmount(item.end?.actualBalance),
          endVariance: renderVariance(item.end?.variance),
          endStatus: renderStatus(item.end?.status),
          cashierPoint: item.cashierPoint?.label || item.cashierPoint?.name || '—',
          userName: item.user?.name || '—',
        })),
      [data]
    );

    const handleChangePage = useCallback(
      (event, newPage) => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', String(newPage + 1));
          return next;
        });
      },
      [setSearchParams]
    );

    const handleChangeRowsPerPage = useCallback(
      (event) => {
        const newLimit = parseInt(event?.target?.value, 10) || 10;
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('limit', String(newLimit));
          next.set('page', '1');
          return next;
        });
      },
      [setSearchParams]
    );

    const columns = useMemo(
      () => [
        { id: 'shiftStartDateTime', label: 'Shift Start At', minWidth: 150 },
        { id: 'startExpectedBalance', label: 'Start Expected', minWidth: 150 },
        { id: 'startActualBalance', label: 'Start Actual', minWidth: 140 },
        { id: 'startVariance', label: 'Start Variance', minWidth: 150 },
        { id: 'startStatus', label: 'Start Status', minWidth: 110 },
        { id: 'shiftEndDateTime', label: 'Shift End At', minWidth: 150 },
        { id: 'endExpectedBalance', label: 'End Expected', minWidth: 150 },
        { id: 'endActualBalance', label: 'End Actual', minWidth: 140 },
        { id: 'endVariance', label: 'End Variance', minWidth: 150 },
        { id: 'endStatus', label: 'End Status', minWidth: 110 },
        { id: 'cashierPoint', label: 'Cashier Point', minWidth: 140 },
        { id: 'userName', label: 'User', minWidth: 120 },
      ],
      []
    );

    const totalCount = pagination?.totalRecords ?? 0;

    if (isLoading) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.loadingWrap}>
            <Loading variant="inline" label="" />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>Unable to load shift summary</p>
            <p className={styles.emptyStateDesc}>{error}</p>
          </div>
        </div>
      );
    }

    if (!transformedData.length) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.emptyState}>
            <PiSmileySad className={styles.emptyStateIcon} aria-hidden />
            <p className={styles.emptyStateTitle}>No shift records found</p>
            <p className={styles.emptyStateDesc}>
              Try adjusting the date range or filters.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <Table
          columns={columns}
          rows={transformedData}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          totalCount={totalCount}
          disableInternalPagination
        />
      </div>
    );
  }
);

ShiftSummaryTable.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  pagination: PropTypes.object,
  setSearchParams: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  limit: PropTypes.number.isRequired,
};

export default ShiftSummaryTable;
